import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const ServiceAreaStep = ({ formData, updateFormData, errors }) => {
  const [newPostalCode, setNewPostalCode] = useState('');

  const dutchProvinces = [
    'Noord-Holland', 'Zuid-Holland', 'Utrecht', 'Noord-Brabant',
    'Gelderland', 'Overijssel', 'Limburg', 'Friesland',
    'Groningen', 'Drenthe', 'Flevoland', 'Zeeland'
  ];

  const handleProvinceToggle = (province) => {
    const currentProvinces = formData?.serviceProvinces || [];
    const updatedProvinces = currentProvinces?.includes(province)
      ? currentProvinces?.filter(p => p !== province)
      : [...currentProvinces, province];
    
    updateFormData({ serviceProvinces: updatedProvinces });
  };

  const handleAddPostalCode = () => {
    if (newPostalCode?.trim()) {
      const currentCodes = formData?.servicePostalCodes || [];
      if (!currentCodes?.includes(newPostalCode?.trim())) {
        updateFormData({ 
          servicePostalCodes: [...currentCodes, newPostalCode?.trim()] 
        });
      }
      setNewPostalCode('');
    }
  };

  const handleRemovePostalCode = (codeToRemove) => {
    const currentCodes = formData?.servicePostalCodes || [];
    updateFormData({ 
      servicePostalCodes: currentCodes?.filter(code => code !== codeToRemove) 
    });
  };

  const handleServiceRadiusChange = (value) => {
    updateFormData({ serviceRadius: value });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-heading font-semibold text-foreground mb-2">
          Werkgebied & Service Locaties
        </h2>
        <p className="text-muted-foreground">
          Definieer waar uw bedrijf diensten levert. Dit helpt klanten u te vinden.
        </p>
      </div>
      {/* Service Radius */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-heading font-medium text-foreground mb-4 flex items-center space-x-2">
          <Icon name="MapPin" size={20} />
          <span>Service Straal</span>
        </h3>
        
        <div className="space-y-4">
          <Input
            label="Maximale service straal (km)"
            type="number"
            placeholder="50"
            description="Hoeveel kilometer bent u bereid te reizen voor projecten?"
            value={formData?.serviceRadius || ''}
            onChange={(e) => handleServiceRadiusChange(e?.target?.value)}
            error={errors?.serviceRadius}
          />
        </div>
      </div>
      {/* Province Selection */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-heading font-medium text-foreground mb-4 flex items-center space-x-2">
          <Icon name="Map" size={20} />
          <span>Provincies</span>
        </h3>
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {dutchProvinces?.map((province) => (
            <div
              key={province}
              className={`border rounded-lg p-3 transition-micro cursor-pointer ${
                (formData?.serviceProvinces || [])?.includes(province)
                  ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
              }`}
              onClick={() => handleProvinceToggle(province)}
            >
              <div className="flex items-center space-x-2">
                <Checkbox
                  checked={(formData?.serviceProvinces || [])?.includes(province)}
                  onChange={() => handleProvinceToggle(province)}
                />
                <span className="text-sm font-medium text-foreground">
                  {province}
                </span>
              </div>
            </div>
          ))}
        </div>
        
        {errors?.serviceProvinces && (
          <div className="text-error text-sm mt-2">
            {errors?.serviceProvinces}
          </div>
        )}
      </div>
      {/* Specific Postal Codes */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-heading font-medium text-foreground mb-4 flex items-center space-x-2">
          <Icon name="Navigation" size={20} />
          <span>Specifieke Postcodes (Optioneel)</span>
        </h3>
        
        <div className="space-y-4">
          <div className="flex space-x-2">
            <div className="flex-1">
              <Input
                label="Postcode toevoegen"
                type="text"
                placeholder="1234 AB"
                value={newPostalCode}
                onChange={(e) => setNewPostalCode(e?.target?.value)}
                description="Voeg specifieke postcodes toe voor gerichte service"
              />
            </div>
            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={handleAddPostalCode}
                iconName="Plus"
                iconPosition="left"
                disabled={!newPostalCode?.trim()}
              >
                Toevoegen
              </Button>
            </div>
          </div>

          {/* Display Added Postal Codes */}
          {(formData?.servicePostalCodes || [])?.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-foreground">
                Toegevoegde Postcodes:
              </h4>
              <div className="flex flex-wrap gap-2">
                {(formData?.servicePostalCodes || [])?.map((code, index) => (
                  <div
                    key={index}
                    className="flex items-center space-x-1 bg-primary/10 text-primary px-2 py-1 rounded-md text-sm"
                  >
                    <span>{code}</span>
                    <button
                      onClick={() => handleRemovePostalCode(code)}
                      className="text-primary hover:text-primary/70 transition-micro"
                    >
                      <Icon name="X" size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Map Preview */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-heading font-medium text-foreground mb-4 flex items-center space-x-2">
          <Icon name="MapPin" size={20} />
          <span>Service Gebied Voorbeeld</span>
        </h3>
        
        <div className="w-full h-64 bg-muted rounded-lg overflow-hidden">
          <iframe
            width="100%"
            height="100%"
            loading="lazy"
            title="Service Gebied Nederland"
            referrerPolicy="no-referrer-when-downgrade"
            src="https://www.google.com/maps?q=52.3676,4.9041&z=7&output=embed"
            className="border-0"
          />
        </div>
        
        <p className="text-sm text-muted-foreground mt-2">
          Dit is een voorbeeld van hoe uw service gebied wordt weergegeven aan klanten.
        </p>
      </div>
    </div>
  );
};

export default ServiceAreaStep;